import { z } from 'zod';

import { AccountPurpose, type ActivityBand, SourceOfFunds, type TradingVolumeBand } from '@/constants/institution';
import { type InstitutionDetailsV2ExpectedActivity, institutionDetailsV2ExpectedActivityBaseSchema, institutionDetailsV2ExpectedActivitySchema } from '@/services/api/profile';
import { addFieldIssue } from '@/utils/field-issues';

export type InstitutionExpectedActivity = {
  netAnnualIncome?: ActivityBand;
  expectedAnnualInvestment?: ActivityBand;
  monthlyFiatDeposits?: ActivityBand;
  monthlyFiatWithdrawals?: ActivityBand;
  monthlyCryptoDeposits?: ActivityBand;
  monthlyCryptoWithdrawals?: ActivityBand;
  monthlyTradingVolume?: TradingVolumeBand;
  accountPurpose: AccountPurpose[];
  accountPurposeOther: string;
  sourceOfFunds: SourceOfFunds[];
  sourceOfFundsOther: string;
  sourceOfFundsDocument?: string;
};

export type InstitutionExpectedActivityOrDefaults = InstitutionExpectedActivity | Partial<InstitutionExpectedActivity>;

export const INSTITUTION_EXPECTED_ACTIVITY_DEFAULTS: InstitutionExpectedActivity = {
  accountPurpose: [],
  accountPurposeOther: '',
  sourceOfFunds: [],
  sourceOfFundsOther: '',
};

const institutionExpectedActivityAccountPurposeSchema = institutionDetailsV2ExpectedActivityBaseSchema
  .pick({ accountPurpose: true })
  .extend({ accountPurposeOther: z.string() })
  .superRefine(({ accountPurpose, accountPurposeOther }, context) => {
    if (accountPurpose.includes(AccountPurpose.OTHER)) {
      addFieldIssue(context, ['accountPurposeOther'], institutionDetailsV2ExpectedActivityBaseSchema.shape.accountPurposeOther.unwrap(), accountPurposeOther);
    }
  });

const institutionExpectedActivitySourceOfFundsSchema = institutionDetailsV2ExpectedActivityBaseSchema
  .pick({ sourceOfFunds: true, sourceOfFundsDocument: true })
  .extend({ sourceOfFundsOther: z.string() })
  .superRefine(({ sourceOfFunds, sourceOfFundsOther }, context) => {
    if (sourceOfFunds.includes(SourceOfFunds.OTHER)) {
      addFieldIssue(context, ['sourceOfFundsOther'], institutionDetailsV2ExpectedActivityBaseSchema.shape.sourceOfFundsOther.unwrap(), sourceOfFundsOther);
    }
  });

export const institutionExpectedActivitySchema = institutionDetailsV2ExpectedActivityBaseSchema
  .pick({
    netAnnualIncome: true,
    expectedAnnualInvestment: true,
    monthlyFiatDeposits: true,
    monthlyFiatWithdrawals: true,
    monthlyCryptoDeposits: true,
    monthlyCryptoWithdrawals: true,
    monthlyTradingVolume: true,
  })
  .and(institutionExpectedActivityAccountPurposeSchema)
  .and(institutionExpectedActivitySourceOfFundsSchema);

// An "Other" description is only kept while Other is selected
export const institutionExpectedActivityToInstitutionDetailsExpectedActivityApi = ({
  accountPurposeOther,
  sourceOfFundsOther,
  ...expectedActivity
}: InstitutionExpectedActivity): Promise<InstitutionDetailsV2ExpectedActivity> =>
  institutionDetailsV2ExpectedActivitySchema.parseAsync({
    ...expectedActivity,
    ...(expectedActivity.accountPurpose.includes(AccountPurpose.OTHER) && { accountPurposeOther }),
    ...(expectedActivity.sourceOfFunds.includes(SourceOfFunds.OTHER) && { sourceOfFundsOther }),
  });

export const institutionDetailsExpectedActivityApiToInstitutionExpectedActivity = (expectedActivity: InstitutionDetailsV2ExpectedActivity): InstitutionExpectedActivity => ({
  ...expectedActivity,
  accountPurposeOther: expectedActivity.accountPurposeOther ?? '',
  sourceOfFundsOther: expectedActivity.sourceOfFundsOther ?? '',
});
